import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useCoordinatorAccess } from '~/hooks/useIsCoordinator';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/mass-messaging',
)({
  component: MassMessagingLayout,
});

/**
 * The guard for the whole Mass Messaging tab: admins and mods of congregations
 * and custom study groups.
 *
 * As in legacy, a platform admin who doesn't lead the group is refused, so this
 * is `useCoordinatorAccess` and not `useGroupAdminAccess`, which is admin-only
 * and lets platform admins through. A component redirect rather than
 * `beforeLoad`, because the answer comes from `getGroupBySlug`.
 */
function MassMessagingLayout(): ReactElement | null {
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { isCoordinator, isResolved } = useCoordinatorAccess();
  // `isResolved` matters: while the group loads, "not known yet" and "refused"
  // look the same, and a bare `!isCoordinator` bounces the group's own
  // coordinator on first paint.
  const refused = isResolved && !isCoordinator;

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return <Outlet />;
}
